// SavedRepliesMenu — Phase 6e "Templates" dropdown for the Composer, extended
// for Polish #15 to group entries into two sections:
//
//   Quick replies  — short, autofilled action templates (tracking, proof,
//                    reship, address change, status update). Body contains
//                    `{name}` / `{order}` / `{tracking}` placeholders that
//                    get resolved at pick time using the context props.
//
//   FAQ answers    — the longer FAQ-based canned replies from Phase 6e.
//                    No autofill.
//
// Renders a "Templates ▾" button next to the Haiku draft + Send buttons.
// Clicking opens a popover with the two sections + eyebrow labels.
// Picking an entry resolves autofill placeholders, fires onPick, and
// dismisses. Parent decides whether to replace or append.
//
// Outside-click + Escape dismissal mirrors BulkActionBar's snooze popover.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  SAVED_REPLIES,
  fillSavedReply,
  missingPlaceholders,
  type SavedReply,
  type SavedReplyContext,
} from '@/lib/savedReplies';

type SavedRepliesMenuProps = {
  /** Disabled while the composer is busy (sending / drafting). */
  disabled?: boolean;
  /**
   * Context for autofill resolution. Passed down from Composer → Center;
   * any missing fields render as `{key}` literals in the body so Kimi
   * sees which variables she still needs to type in.
   */
  context: SavedReplyContext;
  /**
   * Called with the chosen template AND the fully-resolved body (post
   * autofill). Parent decides whether to replace or append.
   */
  onPick: (reply: SavedReply, resolvedBody: string) => void;
};

const KIND_ORDER: Array<{ kind: SavedReply['kind']; label: string }> = [
  { kind: 'action', label: 'Quick replies' },
  { kind: 'faq', label: 'FAQ answers' },
];

export function SavedRepliesMenu({ disabled, context, onPick }: SavedRepliesMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Group once per render. SAVED_REPLIES is static so we don't memoize the
  // grouping itself, but we do memoize the per-item "missing" calculation
  // because it depends on context.
  const grouped = useMemo(() => {
    return KIND_ORDER.map(({ kind, label }) => ({
      kind,
      label,
      items: SAVED_REPLIES.filter((r) => r.kind === kind).map((r) => ({
        reply: r,
        missing: missingPlaceholders(r, context),
      })),
    })).filter((g) => g.items.length > 0);
  }, [context]);

  return (
    <div ref={wrapRef} className="saved-replies-menu-wrap">
      <button
        type="button"
        className="btn"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        title="Insert a saved reply template"
      >
        Templates ▾
      </button>
      {open ? (
        <div role="menu" className="saved-replies-menu-pop">
          {grouped.map((group, gi) => (
            <div key={group.kind} className="saved-replies-section">
              <div
                className="saved-replies-eyebrow"
                style={gi > 0 ? { marginTop: 8 } : undefined}
              >
                {group.label}
              </div>
              {group.items.map(({ reply, missing }) => {
                const resolved = fillSavedReply(reply, context);
                return (
                  <button
                    key={reply.id}
                    type="button"
                    role="menuitem"
                    disabled={disabled}
                    className="saved-replies-item"
                    onClick={() => {
                      setOpen(false);
                      onPick(reply, resolved);
                    }}
                    title={
                      missing.length > 0
                        ? `Missing: ${missing.join(', ')}`
                        : `Insert "${reply.label}"`
                    }
                  >
                    <div className="saved-replies-item-head">
                      <span className="saved-replies-item-label">{reply.label}</span>
                      {missing.length > 0 ? (
                        <span className="saved-replies-item-missing">
                          needs {missing.join(', ')}
                        </span>
                      ) : null}
                    </div>
                    <div className="saved-replies-item-desc">
                      {reply.description}
                      {reply.slash ? (
                        <span className="saved-replies-item-slash"> · /{reply.slash}</span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
